"use client";

import { FunnelChart } from "@/components/charts/funnel-chart";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { useLegendHoverSelect } from "@/lib/hooks/use-hover-select";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ModuleChartFunnelData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { JSX, useMemo } from "react";
import { CHART_PLOT_HEIGHT } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const DEFAULT_DATA: ModuleChartFunnelData = {
  stages: [
    { label: "Visitors", value: 12400, displayValue: "12.4k" },
    { label: "Leads", value: 6800, displayValue: "6.8k" },
    { label: "Qualified", value: 3200, displayValue: "3.2k" },
    { label: "Proposals", value: 1500, displayValue: "1.5k" },
    { label: "Closed", value: 620, displayValue: "620" },
  ],
};

const CHART_HEIGHT = CHART_PLOT_HEIGHT.funnel;

export default function ModuleFunnelChart({
  data = DEFAULT_DATA,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleChartFunnelData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const palette = accentChartColors(grayscale, accent);
  const orientation = data?.orientation ?? "horizontal";
  const rawStages = data?.stages;
  const baseColor = data?.color;
  const stages = useMemo(
    () =>
      (rawStages ?? []).map((s, i) => ({
        ...s,
        label: stripInlineMarkdown(s.label),
        color: s.color ?? baseColor ?? palette[i % palette.length],
      })),
    [rawStages, baseColor, palette],
  );

  const percentBase = useMemo(() => {
    const values = stages.map((s) => (Number.isFinite(s.value) ? Math.max(s.value, 0) : 0));
    return values[0] > 0 ? values[0] : Math.max(0, ...values);
  }, [stages]);

  const legendItems = useMemo(
    () =>
      stages.map((s) => {
        const value = Number.isFinite(s.value) ? Math.max(s.value, 0) : 0;
        return {
          label: s.label,
          value: s.displayValue ?? value,
          percentage: percentBase > 0 ? (value / percentBase) * 100 : 0,
          color: s.color ?? "#000",
        };
      }),
    [stages, percentBase],
  );

  const legend = useLegendHoverSelect();

  return (
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      {stages.length > 0 && (
        <div className="w-full flex items-center justify-center" style={{ height: CHART_HEIGHT }}>
          <FunnelChart
            data={stages}
            orientation={orientation}
            color={baseColor ?? palette[0]}
            layers={data?.layers ?? 3}
            edges={data?.edges ?? "curved"}
            style={orientation === "vertical" ? { aspectRatio: "auto", height: "100%" } : { maxHeight: "100%" }}
            hoveredIndex={legend.effective}
            onHoverChange={legend.setHovered}
            onSegmentClick={(i, element) =>
              legend.select(i, { kind: "segment", index: i, element })
            }
            showLabels
            showValues
            showPercentage
          />
        </div>
      )}

      {legendItems.length > 0 && (
        <SwipeScroll className="w-full pb-1" fade="auto">
          <Legend
            items={legendItems}
            hoveredIndex={legend.hovered}
            onHoverChange={legend.setHovered}
            selectedIndex={legend.selected}
            onSelect={legend.select}
            onLabelRef={legend.setLabelRef}
            className="flex flex-row gap-6"
          >
            <LegendItem className="flex shrink-0 items-center gap-2">
              <LegendMarker className="h-2.5 w-2.5" />
              <LegendLabel />
              <LegendValue
                showPercentage
                className="text-[11px] text-neutral-500 dark:text-neutral-400"
              />
            </LegendItem>
          </Legend>
        </SwipeScroll>
      )}
    </div>
  );
}
